import { useEffect, useMemo, useState } from 'react';
import { Eye, Pencil, Plus, X, Trash2, Copy } from 'lucide-react';
import { getObras, createObra, updateObra, deleteObraApi } from '../services/obraService';
import { getEmpresa } from '../services/empresaService';
import { getContato, createContato } from '../services/contatoService';
import { getSessionClientId, getSessionRole } from '../services/authSession';
import { getCidadesByUf, UF_OPTIONS } from '../services/cidadeService';
import ExpandableSearchFilter from '../components/ExpandableSearchFilter';
import RecordViewModal from '../components/RecordViewModal';
import SearchAutocompleteSelect from '../components/SearchAutocompleteSelect';
import { showSuccessToast, showErrorToast } from '../utils/notifications';
import { onlyDigits, formatCnpj, formatCpf, formatPhone, formatCep, isControlKey } from '../utils/formatters';
import { normalizeContato, normalizeContatoOption, normalizeEmpresa, asArray } from '../utils/normalizers';
import useSubmitLock from '../hooks/useSubmitLock';

const INITIAL_FORM = {
  nomeObra: '', numrCnpj: '', endereco: '', codgCep: '',
  idCidade: '', idEmpresa: '',
  idUsuarioTecnico: '', idUsuarioFinanceiro: '', idUsuarioAdministrativo: '',
  flagRelatorioCor: false, flagObrFotoResultado: false,
  flagReviConcRealizada: false, flagReviCorpoProva: false,
  flagReviResuCorpoProva: false,
};

// Estado inicial do formulário de cadastro rápido de contato (aba do modal).
const INITIAL_CONTACT_FORM = { nomeCompleto: '', numrCpf: '', numrTelefone: '', enderecoEmail: '' };

// Converte uma obra crua da API (snake_case/camelCase/aninhada) no formato da tela.
function normalizeObra(item) {
  if (!item || typeof item !== 'object') return null;
  // Contatos da obra (API sempre em camelCase; aceita id direto ou objeto aninhado).
  const contatoTecnico = item.contatoTecnico || null;
  const contatoFinanceiro = item.contatoFinanceiro || null;
  const contatoAdministrativo = item.contatoAdministrativo || null;
  return {
    idObra: String(item.id_obra || item.idObra || item.id || ''),
    id: String(item.id_obra || item.idObra || item.id || ''),
    nomeObra: item.nome_obra || item.nomeObra || item.nome || '-',
    numrCnpj: String(item.numr_cnpj || item.numrCnpj || item.cnpj || ''),
    endereco: item.endereco || '',
    codgCep: String(item.codg_cep || item.codgCep || item.cep || ''),
    idCidade: String(item.cidade?.id_cidade || item.cidade?.idCidade || ''),
    nomeCidade: item.cidade?.nome_cidade || item.cidade?.nomeCidade || item?.nomeCidade || item?.nome || '',
    idEmpresa: String(item.empresa?.id_empresa || item.empresa?.idEmpresa || ''),
    nomeEmpresa: item.empresa?.nome_empresa || item.empresa?.razaoSocial || item?.nomeEmpresa || item?.nome || '',
    idUsuarioTecnico: String(item.idContatoTecnico || contatoTecnico?.idContato || ''),
    idUsuarioFinanceiro: String(item.idContatoFinanceiro || contatoFinanceiro?.idContato || ''),
    idUsuarioAdministrativo: String(item.idContatoAdministrativo || contatoAdministrativo?.idContato || ''),
    uf: String(item.uf || item.UF || item?.siglaUf || item?.uf || item?.UF || ''),
    flagRelatorioCor: Boolean(item.flag_relatorio_cor ?? item.flagRelatorioCor),
    flagObrFotoResultado: Boolean(item.flag_obr_foto_resultado ?? item.flagObrFotoResultado),
    flagReviConcRealizada: Boolean(item.flag_revi_conc_realizada ?? item.flagReviConcRealizada),
    flagReviCorpoProva: Boolean(item.flag_revi_corpo_prova ?? item.flagReviCorpoProva),
    flagReviResuCorpoProva: Boolean(item.flag_revi_resu_corpo_prova ?? item.flagReviResuCorpoProva),
    ativo: item.ativo !== false,
  };
}

// Resolve o nome de um contato a partir do id; retorna '-' se não achar.
function findLabel(list, id) {
  const item = list.find((entry) => String(entry.id || entry.idContato || entry.id_contato) === String(id));
  return item ? item.nome || item.nomeCompleto : '-';
}

// Tela de Gestão de Obras: lista, busca, cadastra, edita e exclui obras (ações restritas a admin),
// com contatos técnico/financeiro/administrativo e cadastro rápido de contato no modal.
function ObraPage() {
  const role = getSessionRole();
  const sessionClientId = getSessionClientId();
  const canManage = role === 'admin';
  const canCreate = role === 'admin' || role === 'consultor' || !role;

  const [obras, setObras] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [contatos, setContatos] = useState([]);
  const [selectedUf, setSelectedUf] = useState('MT');
  const [cidades, setCidades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeModalTab, setActiveModalTab] = useState('obra');
  const [editingObraId, setEditingObraId] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState({ ...INITIAL_FORM, idCliente: sessionClientId || '' });
  const [contactFormData, setContactFormData] = useState(INITIAL_CONTACT_FORM);
  const [contactFormErrors, setContactFormErrors] = useState({});
  const [viewObra, setViewObra] = useState(null);

  // Contatos reduzidos a { id, nome } para os selects de contato da obra.
  const contatosDisponiveis = useMemo(
    () => contatos.map(normalizeContatoOption).filter((item) => item.id),
    [contatos]
  );

  // Empresa selecionada no formulário, usada pelos botões de copiar CNPJ/endereço.
  const empresaSelecionada = useMemo(
    () => empresas.find((empresa) => empresa.idEmpresa === formData.idEmpresa) || null,
    [empresas, formData.idEmpresa]
  );

  // Carrega as obras da API ao montar.
  useEffect(() => {
    let isMounted = true;
    const loadObras = async () => {
      try {
        const data = await getObras();
        if (isMounted) {
          const normalized = (Array.isArray(data) ? data : [])
            .map(normalizeObra)
            .filter((o) => o?.idObra);
          setObras(normalized);
        }
      } catch (error) {
        if (isMounted) setObras([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    loadObras();
    return () => { isMounted = false; };
  }, []);

  // Carrega as empresas para o select de empresa.
  useEffect(() => {
    let isMounted = true;
    const loadEmpresas = async () => {
      try {
        const data = await getEmpresa();
        if (isMounted) {
          const normalized = asArray(data)
            .map(normalizeEmpresa)
            .filter((e) => e?.idEmpresa);
          setEmpresas(normalized);
        }
      } catch (error) {
        if (isMounted) setEmpresas([]);
      }
    };
    loadEmpresas();
    return () => { isMounted = false; };
  }, []);

  // Carrega os contatos disponíveis para os selects de contato da obra.
  useEffect(() => {
    let isMounted = true;
    const loadContatos = async () => {
      try {
        const data = await getContato();
        if (isMounted) setContatos(Array.isArray(data) ? data.map(normalizeContato).filter(Boolean) : []);
      } catch (error) {
        if (isMounted) setContatos([]);
      }
    };
    loadContatos();
    return () => { isMounted = false; };
  }, []);

  // Recarrega as cidades do formulário sempre que a UF selecionada muda.
  useEffect(() => {
    let isMounted = true;
    const loadCidades = async () => {
      try {
        const data = await getCidadesByUf(selectedUf);
        if (isMounted) setCidades(Array.isArray(data) ? data : []);
      } catch (error) {
        if (isMounted) setCidades([]);
      }
    };
    loadCidades();
    return () => { isMounted = false; };
  }, [selectedUf]);

  const filteredObras = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return obras;
    return obras.filter((obra) => {
      const nome = obra.nomeObra || '';
      const cnpj = obra.numrCnpj || '';
      const endereco = obra.endereco || '';
      return (
        nome.toLowerCase().includes(normalized) ||
        cnpj.toLowerCase().includes(normalized) ||
        endereco.toLowerCase().includes(normalized)
      );
    });
  }, [query, obras]);

  const stats = useMemo(() => ({
    total: obras.length,
    ativas: obras.filter((o) => o.ativo).length,
    comRelatorio: obras.filter((o) => o.flagRelatorioCor).length,
  }), [obras]);

  // Handler de onChange por campo (checkbox usa `checked`, demais usam `value`).
  const handleFieldChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Handler de onChange que aplica uma máscara (CNPJ/CEP) antes de salvar.
  const handleMaskedChange = (field, formatter) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: formatter(event.target.value) }));
  };

  // Copia o CNPJ da empresa selecionada para o campo de CNPJ da obra.
  const handleCopiarCnpjEmpresa = () => {
    if (!empresaSelecionada) return;
    if (!empresaSelecionada.numrCnpj) {
      showErrorToast('A empresa selecionada não possui CNPJ cadastrado.');
      return;
    }
    setFormData((prev) => ({ ...prev, numrCnpj: formatCnpj(empresaSelecionada.numrCnpj) }));
  };

  // Copia endereço, CEP, UF e cidade da empresa selecionada para a obra.
  const handleCopiarEnderecoEmpresa = () => {
    if (!empresaSelecionada) return;
    setFormData((prev) => ({
      ...prev,
      endereco: empresaSelecionada.endereco || '',
      codgCep: formatCep(empresaSelecionada.codgCep || ''),
      idCidade: empresaSelecionada.idCidade || '',
    }));
    setSelectedUf(empresaSelecionada.uf || 'MT');
  };

  // Abre o modal em modo de criação (reseta formulário, aba e UF padrão MT).
  const openModal = () => {
    setFormErrors({});
    setContactFormErrors({});
    setActiveModalTab('obra');
    setEditingObraId(null);
    setSelectedUf('MT');
    setContactFormData(INITIAL_CONTACT_FORM);
    setFormData({ ...INITIAL_FORM, idCliente: sessionClientId || '' });
    setIsModalOpen(true);
  };

  // Abre o modal em modo de edição: carrega cidades da UF e preenche o formulário (inclui contatos).
  const openEditModal = async (obra) => {
    const uf = (obra.uf || obra.UF || 'MT').toUpperCase();
    setFormErrors({});
    setContactFormErrors({});
    setActiveModalTab('obra');
    setEditingObraId(obra.id);
    setSelectedUf(uf);
    setContactFormData(INITIAL_CONTACT_FORM);
    try {
      const data = await getCidadesByUf(uf);
      setCidades(Array.isArray(data) ? data : []);
    } catch (_) {
      setCidades([]);
    }
    setFormData({
      nomeObra: obra.nomeObra || '',
      numrCnpj: formatCnpj(obra.numrCnpj || ''),
      endereco: obra.endereco || '',
      codgCep: formatCep(String(obra.codgCep || '')),
      idCidade: obra.idCidade || '',
      idCliente: obra.idCliente || sessionClientId || '',
      idEmpresa: obra.idEmpresa || '',
      idUsuarioTecnico: obra.idUsuarioTecnico || '',
      idUsuarioFinanceiro: obra.idUsuarioFinanceiro || '',
      idUsuarioAdministrativo: obra.idUsuarioAdministrativo || '',
      flagRelatorioCor: obra.flagRelatorioCor || false,
      flagObrFotoResultado: obra.flagObrFotoResultado || false,
      flagReviConcRealizada: obra.flagReviConcRealizada || false,
      flagReviCorpoProva: obra.flagReviCorpoProva || false,
      flagReviResuCorpoProva: obra.flagReviResuCorpoProva || false,
    });
    setIsModalOpen(true);
  };

  // Fecha o modal e limpa aba, edição e erros.
  const closeModal = () => {
    setIsModalOpen(false);
    setActiveModalTab('obra');
    setEditingObraId(null);
    setFormErrors({});
    setContactFormErrors({});
  };

  // Handler de onChange dos campos do form de contato (aplica máscara no telefone).
  const handleContactFieldChange = (field) => (event) => {
    const value = field === 'numrTelefone' ? formatPhone(event.target.value) : event.target.value;
    setContactFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Valida e cria um novo contato pela aba do modal, adicionando-o aos selects de contato.
  const [submittingContato, handleAddContato] = useSubmitLock(async () => {
    const errors = {};
    const telefoneDigits = onlyDigits(contactFormData.numrTelefone);
    const email = String(contactFormData.enderecoEmail || '').trim();

    if (!contactFormData.nomeCompleto.trim()) errors.nomeCompleto = 'Nome obrigatório.';

    if (!telefoneDigits) errors.numrTelefone = 'Telefone obrigatório.';
    else if (telefoneDigits.length < 10 || telefoneDigits.length > 11) errors.numrTelefone = 'Telefone inválido.';

    if (!email) errors.enderecoEmail = 'Email obrigatório.';
    else if (!/\S+@\S+\.\S+/.test(email)) errors.enderecoEmail = 'Email inválido.';

    if (Object.keys(errors).length > 0) {
      setContactFormErrors(errors);
      return;
    }

    try {
      const effectiveClientId = formData.idCliente || sessionClientId || null;
      const { response, payload } = await createContato(
        {
          nomeCompleto: contactFormData.nomeCompleto.trim(),
          numrCpf: contactFormData.numrCpf,
          numrTelefone: telefoneDigits,
          enderecoEmail: email,
        },
        effectiveClientId
      );

      const novoContato = normalizeContato(
        response || {
          id: `contato-local-${Date.now()}`,
          ...payload,
          nomeCompleto: contactFormData.nomeCompleto.trim(),
          numrCpf: onlyDigits(contactFormData.numrCpf),
          numrTelefone: telefoneDigits,
          enderecoEmail: email,
        }
      );

      setContatos((prev) => [novoContato, ...prev]);
      setContactFormErrors({});
      setContactFormData(INITIAL_CONTACT_FORM);
      showSuccessToast('Contato cadastrado com sucesso.');
      setActiveModalTab('obra');
    } catch (error) {
      setContactFormErrors({ geral: error?.message || 'Não foi possível salvar o contato.' });
    }
  });

  // Valida obrigatórios e formato de CNPJ/CEP; retorna erros + dígitos sem máscara.
  const validateForm = () => {
    const errors = {};
    if (!formData.nomeObra.trim()) errors.nomeObra = 'Nome da obra obrigatório.';
    if (!formData.numrCnpj.trim()) errors.numrCnpj = 'CNPJ obrigatório.';
    if (!String(formData.idCidade || '').trim()) errors.idCidade = 'Cidade obrigatória.';
    if (!String(formData.idEmpresa || '').trim()) errors.idEmpresa = 'Empresa obrigatória.';
    if (!String(formData.idCliente || '').trim()) errors.geral = 'Cliente da sessão não identificado.';

    const cnpjDigits = onlyDigits(formData.numrCnpj);
    if (formData.numrCnpj.trim() && cnpjDigits.length !== 14) errors.numrCnpj = 'CNPJ inválido.';

    const cepDigits = onlyDigits(formData.codgCep);
    if (cepDigits && cepDigits.length !== 8) errors.codgCep = 'CEP inválido.';

    return { errors, cnpjDigits, cepDigits };
  };

  // Submete o form: valida, checa CNPJ duplicado, cria/atualiza na API e refaz a lista.
  const [submitting, handleSaveObra] = useSubmitLock(async (event) => {
    event.preventDefault();
    const { errors, cnpjDigits, cepDigits } = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const cnpjExists = obras.some((obra) => {
      if (editingObraId && obra.id === editingObraId) return false;
      return (obra.numrCnpj || '') === cnpjDigits;
    });

    if (cnpjExists) {
      setFormErrors({ numrCnpj: 'CNPJ já cadastrado.' });
      return;
    }

    try {
      if (editingObraId) {
        const { response, payload } = await updateObra(editingObraId, formData, cnpjDigits, cepDigits);
        const updatedObra = normalizeObra({ ...payload, ...response, id: editingObraId });
        setObras((prev) =>
          prev.map((obra) => (obra.id === editingObraId ? { ...obra, ...updatedObra } : obra))
        );
        showSuccessToast('Obra atualizada com sucesso.');
      } else {
        const { response: novaObra, payload } = await createObra(formData, cnpjDigits, cepDigits);
        const normalizedObra = normalizeObra({
          ...payload,
          ...novaObra,
          id: novaObra?.id || novaObra?.idObra || `obra-${Date.now()}`,
          ativo: true,
        });
        setObras((prev) => [normalizedObra, ...prev]);
        showSuccessToast('Obra cadastrada com sucesso.');
      }

      setFormErrors({});
      closeModal();
    } catch (error) {
      console.error('Erro ao salvar obra:', error);
      setFormErrors({ geral: error?.message || 'Não foi possível salvar a obra.' });
    }
  });

  // Exclui a obra após confirmação (restrito a admin) e remove da lista local.
  const deleteObra = async (obraId) => {
    if (!canManage) return;

    const confirmar = window.confirm("Tem certeza que deseja excluir esta obra?");
    if (!confirmar) return;

    try {
      await deleteObraApi(obraId);
      setObras((prev) => prev.filter((obra) => obra.id !== obraId));
      showSuccessToast('Obra excluída com sucesso.');
    } catch (error) {
      console.error('Erro ao excluir obra:', error);
      showErrorToast(error?.message || 'Não foi possível excluir a obra.');
    }
  };

  // Quantidade de colunas da tabela (para skeleton e estado vazio). A coluna de ações
  // agora existe sempre (o botão Visualizar aparece para todos os perfis).
  const columnCount = 8;

  return (
    <div className="obras-page space-y-6">
      <section className="obras-header flex flex-col gap-4 rounded-2xl p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between bg-white">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900 sm:text-3xl">Gestão de obras</h2>
          <p className="mt-2 max-w-2xl text-sm text-slate-500">
            Visualize e gerencie os projetos de obra cadastrados no sistema.
          </p>
        </div>
        <button
          type="button"
          onClick={openModal}
          className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm transition hover:border-blue-200 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
          disabled={!canCreate}
        >
          <Plus className="h-4 w-4" />
          Nova obra
        </button>
      </section>

      <section className="obras-metrics grid gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Total</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">{stats.total}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Ativas</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">{stats.ativas}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Com relatório</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">{stats.comRelatorio}</p>
        </div>
      </section>

      <section className="obras-table overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-4 py-3 sm:hidden">
          <ExpandableSearchFilter
            query={query}
            onChange={setQuery}
            placeholder="Buscar por nome, CNPJ ou endereço"
            resultCount={filteredObras.length}
          />
        </div>

        <div className="hidden overflow-x-auto sm:block">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-[0.3em] text-slate-400">
              <tr>
                <th className="px-4 py-3">Nome</th>
                <th className="px-4 py-3">CNPJ</th>
                <th className="px-4 py-3">Endereço</th>
                <th className="px-4 py-3">Contato técnico</th>
                <th className="px-4 py-3">Contato financeiro</th>
                <th className="px-4 py-3">Contato administrativo</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <span>Ações</span>
                    <ExpandableSearchFilter
                      variant="inline"
                      query={query}
                      onChange={setQuery}
                      placeholder="Buscar..."
                      resultCount={filteredObras.length}
                    />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <tr key={`skeleton-${index}`} className="border-b border-slate-100">
                    {Array.from({ length: columnCount }).map((__, cellIndex) => (
                      <td key={cellIndex} className="px-4 py-4">
                        <div className="h-4 w-full animate-pulse rounded bg-slate-100" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : filteredObras.length === 0 ? (
                <tr>
                  <td
                    colSpan={columnCount}
                    className="px-4 py-6 text-center text-sm text-slate-500"
                  >
                    Nenhuma obra encontrada.
                  </td>
                </tr>
              ) : (
                filteredObras.map((obra) => (
                  <tr key={obra.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/80">
                    <td className="px-4 py-4">
                      <div className="font-medium text-slate-900">{obra.nomeObra}</div>
                    </td>
                    <td className="px-4 py-4 text-slate-600">
                      {formatCnpj(String(obra.numrCnpj || ''))}
                    </td>
                    <td className="px-4 py-4 text-slate-600 text-xs">{obra.endereco}</td>
                    <td className="px-4 py-4 text-slate-600">{findLabel(contatosDisponiveis, obra.idUsuarioTecnico)}</td>
                    <td className="px-4 py-4 text-slate-600">{findLabel(contatosDisponiveis, obra.idUsuarioFinanceiro)}</td>
                    <td className="px-4 py-4 text-slate-600">{findLabel(contatosDisponiveis, obra.idUsuarioAdministrativo)}</td>
                    <td className="px-4 py-4">
                      <span
                        className={`rounded-full px-2.5 py-1 text-xs font-medium ${obra.ativo
                          ? 'bg-[#0e7490]/10 text-[#0e7490]'
                          : 'bg-slate-100 text-slate-600'
                          }`}
                      >
                        {obra.ativo ? 'Ativa' : 'Inativa'}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setViewObra(obra)}
                          className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-[#0e7490]/40 hover:text-[#0e7490]"
                        >
                          <Eye className="h-3.5 w-3.5" />
                          Visualizar
                        </button>
                        {canManage ? (
                          <>
                            <button
                              type="button"
                              onClick={() => openEditModal(obra)}
                              className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                              Editar
                            </button>
                            <button
                              type="button"
                              onClick={() => deleteObra(obra.id)}
                              className="inline-flex items-center gap-1 rounded-full border border-red-200 px-2.5 py-1 text-xs font-medium text-red-600 transition hover:border-red-300 hover:bg-red-50"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                              Excluir
                            </button>
                          </>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="space-y-3 p-4 sm:hidden">
          {loading ? (
            Array.from({ length: 4 }).map((_, index) => (
              <div key={`mobile-skeleton-${index}`} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="h-4 w-1/2 animate-pulse rounded bg-slate-100" />
                <div className="mt-3 h-3 w-2/3 animate-pulse rounded bg-slate-100" />
                <div className="mt-3 h-3 w-1/3 animate-pulse rounded bg-slate-100" />
              </div>
            ))
          ) : filteredObras.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
              Nenhuma obra encontrada.
            </div>
          ) : (
            filteredObras.map((obra) => (
              <div key={obra.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-base font-semibold text-slate-900">{obra.nomeObra}</p>
                    <p className="text-xs text-slate-500">{formatCnpj(String(obra.numrCnpj || ''))}</p>
                  </div>
                  <span
                    className={`rounded-full px-2 py-1 text-xs font-medium ${obra.ativo
                      ? 'bg-[#0e7490]/10 text-[#0e7490]'
                      : 'bg-slate-100 text-slate-600'
                      }`}
                  >
                    {obra.ativo ? 'Ativa' : 'Inativa'}
                  </span>
                </div>
                <div className="mt-3 grid grid-cols-1 gap-1 text-xs text-slate-600">
                  <span>Endereço: {obra.endereco || '-'}</span>
                  <span>Contato técnico: {findLabel(contatosDisponiveis, obra.idUsuarioTecnico)}</span>
                  <span>Contato financeiro: {findLabel(contatosDisponiveis, obra.idUsuarioFinanceiro)}</span>
                  <span>Contato administrativo: {findLabel(contatosDisponiveis, obra.idUsuarioAdministrativo)}</span>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setViewObra(obra)}
                    className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-[#0e7490]/40 hover:text-[#0e7490]"
                  >
                    <Eye className="h-3.5 w-3.5" />
                    Visualizar
                  </button>
                  {canManage ? (
                    <>
                      <button
                        type="button"
                        onClick={() => openEditModal(obra)}
                        className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                        Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteObra(obra.id)}
                        className="inline-flex items-center gap-1 rounded-full border border-red-200 px-2.5 py-1 text-xs font-medium text-red-600 transition hover:border-red-300 hover:bg-red-50"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Excluir
                      </button>
                    </>
                  ) : null}
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      <RecordViewModal
        open={!!viewObra}
        onClose={() => setViewObra(null)}
        title="Detalhes da obra"
        sections={viewObra ? [
          {
            title: 'Dados gerais',
            fields: [
              { label: 'Nome da obra', value: viewObra.nomeObra || '-' },
              { label: 'CNPJ', value: formatCnpj(String(viewObra.numrCnpj || '')) || '-' },
              { label: 'Empresa', value: viewObra.nomeEmpresa || viewObra.idEmpresa || '-' },
              { label: 'Status', value: viewObra.ativo ? 'Ativa' : 'Inativa' },
            ],
          },
          {
            title: 'Endereço',
            fields: [
              { label: 'Endereço', value: viewObra.endereco || '-' },
              { label: 'CEP', value: formatCep(String(viewObra.codgCep || '')) || '-' },
              { label: 'Cidade', value: viewObra.nomeCidade || viewObra.idCidade || '-' },
              { label: 'UF', value: viewObra.uf || '-' },
            ],
          },
          {
            title: 'Contatos',
            fields: [
              { label: 'Contato técnico', value: findLabel(contatosDisponiveis, viewObra.idUsuarioTecnico) },
              { label: 'Contato financeiro', value: findLabel(contatosDisponiveis, viewObra.idUsuarioFinanceiro) },
              { label: 'Contato administrativo', value: findLabel(contatosDisponiveis, viewObra.idUsuarioAdministrativo) },
            ],
          },
          {
            title: 'Configurações',
            columns: 2,
            fields: [
              { label: 'Relatório em cor', value: viewObra.flagRelatorioCor ? 'Sim' : 'Não' },
              { label: 'Foto de resultado', value: viewObra.flagObrFotoResultado ? 'Sim' : 'Não' },
              { label: 'Revisar concretagem realizada', value: viewObra.flagReviConcRealizada ? 'Sim' : 'Não' },
              { label: 'Revisar corpo de prova', value: viewObra.flagReviCorpoProva ? 'Sim' : 'Não' },
              { label: 'Revisar resultado CP', value: viewObra.flagReviResuCorpoProva ? 'Sim' : 'Não' },
            ],
          },
        ] : []}
      />

      {isModalOpen ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center px-4 py-4 sm:py-0">
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/40"
            onClick={closeModal}
            aria-label="Fechar modal"
          />
          <form
            onSubmit={handleSaveObra}
            className="relative z-10 w-full max-w-sm sm:max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-4 shadow-xl sm:p-6"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Cadastro</p>
                <h3 className="text-xl font-semibold text-slate-900">
                  {editingObraId ? 'Editar obra' : 'Cadastrar obra'}
                </h3>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                aria-label="Fechar"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {formErrors.geral ? (
              <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {formErrors.geral}
              </div>
            ) : null}

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setActiveModalTab('obra')}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${activeModalTab === 'obra'
                  ? 'border-[#0e7490] bg-[#0e7490]/10 text-[#0e7490]'
                  : 'border-slate-200 text-slate-600 hover:border-blue-200 hover:text-blue-600'
                  }`}
              >
                Dados da obra
              </button>
              <button
                type="button"
                onClick={() => setActiveModalTab('contato')}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${activeModalTab === 'contato'
                  ? 'border-[#0e7490] bg-[#0e7490]/10 text-[#0e7490]'
                  : 'border-slate-200 text-slate-600 hover:border-blue-200 hover:text-blue-600'
                  }`}
              >
                Cadastro de contato
              </button>
            </div>

            {activeModalTab === 'obra' ? (
              <div className="mt-6 space-y-3 sm:space-y-4">
                <div className="space-y-2">
                  <label htmlFor="obra-empresa" className="text-sm font-medium text-slate-700">
                    Empresa <span className="text-rose-500">*</span>
                  </label>
                  <select
                    id="obra-empresa"
                    value={formData.idEmpresa}
                    onChange={handleFieldChange('idEmpresa')}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  >
                    <option value="">Selecione</option>
                    {empresas.map((empresa) => (
                      <option key={String(empresa.idEmpresa ?? empresa.id ?? '')} value={String(empresa.idEmpresa ?? empresa.id ?? '')}>
                        {empresa.razaoSocial || empresa.nomeEmpresa || empresa.nome}
                      </option>
                    ))}
                  </select>
                  {formErrors.idEmpresa ? (
                    <p className="text-xs text-rose-600">{formErrors.idEmpresa}</p>
                  ) : null}
                </div>

                <div className="space-y-2">
                  <label htmlFor="obra-nome" className="text-sm font-medium text-slate-700">
                    Nome da obra:
                  </label>
                  <input
                    id="obra-nome"
                    type="text"
                    value={formData.nomeObra}
                    onChange={handleFieldChange('nomeObra')}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                  {formErrors.nomeObra ? (
                    <p className="text-xs text-rose-600">{formErrors.nomeObra}</p>
                  ) : null}
                </div>

                <div className="space-y-2">
                  <label htmlFor="obra-cnpj" className="text-sm font-medium text-slate-700">
                    CNPJ <span className="text-rose-500">*</span>
                  </label>
                  <div className="flex items-end gap-2">
                    <input
                      id="obra-cnpj"
                      type="text"
                      value={formData.numrCnpj}
                      onChange={handleMaskedChange('numrCnpj', formatCnpj)}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                    <button
                      type="button"
                      onClick={handleCopiarCnpjEmpresa}
                      disabled={!empresaSelecionada}
                      title={empresaSelecionada ? 'Copiar CNPJ da empresa' : 'Selecione uma empresa'}
                      className="flex flex-none items-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-600 hover:bg-blue-600 hover:text-white disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-blue-50 disabled:hover:text-blue-600"
                    >
                      <Copy className="h-3.5 w-3.5" />
                      Copiar CNPJ
                    </button>
                  </div>
                  {formErrors.numrCnpj ? (
                    <p className="text-xs text-rose-600">{formErrors.numrCnpj}</p>
                  ) : null}
                </div>

                <div className="space-y-2">
                  <label htmlFor="obra-endereco" className="text-sm font-medium text-slate-700">
                    Endereço
                  </label>
                  <div className="flex items-end gap-2">
                    <input
                      id="obra-endereco"
                      type="text"
                      value={formData.endereco}
                      onChange={handleFieldChange('endereco')}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                    <button
                      type="button"
                      onClick={handleCopiarEnderecoEmpresa}
                      disabled={!empresaSelecionada}
                      title={empresaSelecionada ? 'Copiar endereço da empresa' : 'Selecione uma empresa'}
                      className="flex flex-none items-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-600 hover:bg-blue-600 hover:text-white disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-blue-50 disabled:hover:text-blue-600"
                    >
                      <Copy className="h-3.5 w-3.5" />
                      Copiar endereço
                    </button>
                  </div>
                </div>

                <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-3">
                  <div className="space-y-2">
                    <label htmlFor="obra-cep" className="text-sm font-medium text-slate-700">
                      CEP:
                    </label>
                    <input
                      id="obra-cep"
                      type="text"
                      inputMode="numeric"
                      value={formData.codgCep}
                      onChange={handleMaskedChange('codgCep', formatCep)}
                      onKeyDown={(event) => {
                        if (isControlKey(event)) return;
                        if (event.key.length === 1 && !/\d/.test(event.key)) event.preventDefault();
                      }}
                      onPaste={(event) => {
                        const pasted = event.clipboardData.getData('text');
                        if (/\D/.test(pasted)) event.preventDefault();
                      }}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                    {formErrors.codgCep ? (
                      <p className="text-xs text-rose-600">{formErrors.codgCep}</p>
                    ) : null}
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="obra-uf" className="text-sm font-medium text-slate-700">
                      UF <span className="text-rose-500">*</span>
                    </label>
                    <select
                      id="obra-uf"
                      value={selectedUf}
                      onChange={(event) => {
                        const uf = event.target.value;
                        setSelectedUf(uf);
                        setFormData((prev) => ({ ...prev, idCidade: '' }));
                      }}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    >
                      {UF_OPTIONS.map((uf) => (
                        <option key={uf} value={uf}>{uf}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="obra-cidade" className="text-sm font-medium text-slate-700">
                      Cidade <span className="text-rose-500">*</span>
                    </label>
                    <SearchAutocompleteSelect
                      id="obra-cidade"
                      value={formData.idCidade}
                      onChange={handleFieldChange('idCidade')}
                      placeholder="Buscar cidade"
                      options={cidades.map((cidade) => ({
                        id: cidade.idCidade || cidade.id,
                        label: cidade.nomeCidade || cidade.nome,
                      }))}
                    />
                    {formErrors.idCidade ? (
                      <p className="text-xs text-rose-600">{formErrors.idCidade}</p>
                    ) : null}
                  </div>
                </div>

                <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-3">
                  <div className="space-y-2">
                    <label htmlFor="obra-tecnico" className="text-sm font-medium text-slate-700">
                      Contato técnico:
                    </label>
                    <select
                      id="obra-tecnico"
                      value={formData.idUsuarioTecnico}
                      onChange={handleFieldChange('idUsuarioTecnico')}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    >
                      <option value="">Selecione</option>
                      {contatosDisponiveis.map((contato) => (
                        <option key={contato.id} value={contato.id}>{contato.nome}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="obra-financeiro" className="text-sm font-medium text-slate-700">
                      Contato financeiro:
                    </label>
                    <select
                      id="obra-financeiro"
                      value={formData.idUsuarioFinanceiro}
                      onChange={handleFieldChange('idUsuarioFinanceiro')}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    >
                      <option value="">Selecione</option>
                      {contatosDisponiveis.map((contato) => (
                        <option key={contato.id} value={contato.id}>{contato.nome}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="obra-administrativo" className="text-sm font-medium text-slate-700">
                      Contato administrativo:
                    </label>
                    <select
                      id="obra-administrativo"
                      value={formData.idUsuarioAdministrativo}
                      onChange={handleFieldChange('idUsuarioAdministrativo')}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    >
                      <option value="">Selecione</option>
                      {contatosDisponiveis.map((contato) => (
                        <option key={contato.id} value={contato.id}>{contato.nome}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <p className="text-sm font-medium text-slate-700">Configurações</p>
                  <div className="space-y-3">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.flagRelatorioCor}
                        onChange={handleFieldChange('flagRelatorioCor')}
                        className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-2"
                      />
                      <span className="text-sm text-slate-700">Relatório em cor</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.flagObrFotoResultado}
                        onChange={handleFieldChange('flagObrFotoResultado')}
                        className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-2"
                      />
                      <span className="text-sm text-slate-700">Foto de resultado</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.flagReviConcRealizada}
                        onChange={handleFieldChange('flagReviConcRealizada')}
                        className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-2"
                      />
                      <span className="text-sm text-slate-700">Revisar concretagem realizada</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.flagReviCorpoProva}
                        onChange={handleFieldChange('flagReviCorpoProva')}
                        className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-2"
                      />
                      <span className="text-sm text-slate-700">Revisar corpo de prova</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.flagReviResuCorpoProva}
                        onChange={handleFieldChange('flagReviResuCorpoProva')}
                        className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-2"
                      />
                      <span className="text-sm text-slate-700">Revisar resultado CP</span>
                    </label>
                  </div>
                </div>

                <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 sm:gap-3 pt-2">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="rounded-md border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600 w-full sm:w-auto"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60 w-full sm:w-auto"
                  >
                    {submitting ? 'Salvando...' : editingObraId ? 'Salvar alterações' : 'Salvar obra'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-6 space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Nome completo <span className="text-rose-500">*</span></label>
                    <input
                      type="text"
                      value={contactFormData.nomeCompleto}
                      onChange={handleContactFieldChange('nomeCompleto')}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                    />
                    {contactFormErrors.nomeCompleto ? <p className="text-xs text-rose-600">{contactFormErrors.nomeCompleto}</p> : null}
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">CPF:</label>
                    <input
                      type="text"
                      value={contactFormData.numrCpf}
                      onChange={(event) => setContactFormData((prev) => ({ ...prev, numrCpf: formatCpf(event.target.value) }))}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Telefone <span className="text-rose-500">*</span></label>
                    <input
                      type="text"
                      value={contactFormData.numrTelefone}
                      onChange={handleContactFieldChange('numrTelefone')}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                    />
                    {contactFormErrors.numrTelefone ? <p className="text-xs text-rose-600">{contactFormErrors.numrTelefone}</p> : null}
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Email <span className="text-rose-500">*</span></label>
                    <input
                      type="email"
                      value={contactFormData.enderecoEmail}
                      onChange={handleContactFieldChange('enderecoEmail')}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                    />
                    {contactFormErrors.enderecoEmail ? <p className="text-xs text-rose-600">{contactFormErrors.enderecoEmail}</p> : null}
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                  O contato cadastrado fica disponível nos campos de contato técnico, financeiro e administrativo.
                </div>
                {contactFormErrors.geral ? (
                  <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm font-medium text-rose-600">
                    {contactFormErrors.geral}
                  </div>
                ) : null}
                <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    onClick={() => setActiveModalTab('obra')}
                    className="w-full rounded-md border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600 sm:w-auto"
                  >
                    Voltar para obra
                  </button>
                  <button
                    type="button"
                    onClick={handleAddContato}
                    disabled={submittingContato}
                    className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                  >
                    {submittingContato ? 'Salvando...' : 'Salvar contato'}
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
      ) : null}
    </div>
  );
}

export default ObraPage;
